/**
 * "How to get your key" help, one guide per vault provider id.
 *
 * Text only. The components that render it (`ProviderSetupGuide.tsx`) hold no
 * wording, so a copy change touches this file and nothing else.
 *
 * This is NOT a provider list. Which providers a customer can add is still the
 * server's `providers` catalog on `GET /v1/provider-keys` (built from
 * `auteur_cloud/vault/keystore.PROVIDERS`); a guide is shown only for an id the
 * server listed, and an id with no guide simply shows none.
 *
 * Every "what it powers" line is taken from what the code runs today:
 * the chat relay (`gateway.SUPPORTED_PROVIDERS`: openai, anthropic, openrouter)
 * and the media catalog (`gateway/media_catalog.py`: openai images, WaveSpeed
 * text-to-video, ElevenLabs speech/dialogue/sound effects/music, HeyGen avatar
 * video). Providers with nothing built yet say so (`connections/readiness.py`
 * KEY_ROADMAP). Links are the providers' own pages, checked 2026-10-08; labels
 * are quoted only where the provider's docs name them.
 *
 * Nothing here ever holds, logs or echoes a key.
 */

export interface GuideLink {
  label: string;
  href: string;
}

export interface GuideStep {
  text: string;
  /** Rendered after `text`, opening in a new tab. */
  link?: GuideLink;
}

export interface ProviderSetupGuide {
  /** Vault provider id (`keystore.PROVIDERS`). */
  id: string;
  name: string;
  /** One plain line: what the key does in Auteur today. */
  powers: string;
  /** Numbered steps to get a key, in order. */
  steps: GuideStep[];
  /** Who pays, and anything the provider needs before the first call. */
  billing: string;
  /** "New to this?" track. */
  signUp: GuideLink | null;
  billingPage: GuideLink | null;
  pricingPage: GuideLink | null;
  /** Only what the provider publishes; otherwise null and the pricing link is shown. */
  firstUseCost: string | null;
  /** Pro track: key page and scope or permission notes. */
  keyPage: GuideLink | null;
  pro: string;
  /** The provider's own help page for API keys. */
  helpPage: GuideLink | null;
  /** Where to revoke or replace a key (usually the key page). */
  revokePage: GuideLink | null;
}

/** Shared text, used by every guide. */
export const GUIDE_TEXT = {
  toggle: 'How to get your key',
  newToThisToggle: 'New to this? Start here',
  whatIsAKey:
    'An API key is a long password that lets Auteur use your account with that provider, ' +
    'so the work is charged to you and not to anyone else.',
  noAccount: "Don't have an account yet?",
  addCredit: 'Add credit or billing:',
  firstUseCostFallback: "Costs depend on what you make. Check the provider's pricing page:",
  whoPaysTitle: 'Who pays',
  whoPaysCommon:
    'Usage is billed to your provider account, not to Auteur. Auteur only uses a key in a ' +
    'workspace while "Allow my workspace to use this key for chat and generation" is on beside ' +
    'it (it starts on when you add a key, and you can turn it off at any time), and you can set ' +
    'optional generation limits under the key.',
  pasteTitle: 'Paste it here',
  paste:
    'Under "Add a key", pick this provider in its group, give it a label if you like (for ' +
    'example "Auteur"), paste the key into the "Key" field and press "Store key". The key is ' +
    'stored encrypted in your workspace vault and is never shown again in full, only its last ' +
    'few characters.',
  pasteFromConnections:
    'Open Settings → API keys (the "Manage keys" button below), choose this provider under ' +
    '"Add a key", paste the key into the "Key" field and press "Store key". It is stored ' +
    'encrypted in your workspace vault and never shown again in full.',
  checkTitle: 'How to check it worked',
  checkProbe:
    'The key appears under "Stored keys". Press "Test this key" for a free check: the light turns ' +
    'green ("Connected") when the provider accepts it. The check is optional for writing and ' +
    'generation; image analysis (vision) only runs with a key that has passed it. Settings → ' +
    'Connections then shows "Ready in this workspace" once the workspace is allowed to use it.',
  checkNoProbe:
    'The key appears under "Stored keys". Auteur can\'t check this provider\'s keys ' +
    'automatically, so its status stays "Auteur can\'t check this key automatically"; that ' +
    'does not mean the key is wrong. Settings → Connections shows what it can run.',
  proTitle: 'Already know your way around?',
  helpTitle: 'Official help',
  safetyTitle: 'Keep it safe',
  safety: [
    'Never share your key or paste it into chats, emails or documents. Anyone with it can spend your credit.',
    'If it may have leaked, revoke it on the provider\'s page and create a new one, then use "Replace key" here.',
    'If the light is red ("Rejected / not working"), the provider refused the key: it may be mistyped, ' +
      'revoked or expired, or lack a permission it needs. Check it on the provider\'s page, or create a ' +
      'new one and use "Replace key". "Provider unreachable" means the check did not get a clear answer ' +
      '(for example the provider was busy or down); try "Test this key" again later.',
  ],
  revokeLinkLabel: 'Revoke or replace it here',
  newTab: '(opens in a new tab)',
} as const;

/** The onboarding "Getting started" overview: which key matters for what. */
export const GETTING_STARTED = {
  title: 'Getting started: which keys do I need?',
  intro:
    'You only need a key for the kind of work you want to do. Every key is optional: skip this ' +
    'step and add keys later in Settings → API keys.',
  rows: [
    { goal: 'Writing (books, scripts, Tasks)', providers: 'OpenRouter, OpenAI or Anthropic. One is enough; OpenRouter gives you many models with one key.' },
    { goal: 'Video from a text prompt', providers: 'WaveSpeed (Seedance, Kling, Veo and Grok Imagine models).' },
    { goal: 'Narration, voices, sound effects and music', providers: 'ElevenLabs.' },
    { goal: 'A talking presenter (avatar video)', providers: 'HeyGen.' },
    { goal: 'Images', providers: 'OpenAI.' },
  ],
  footer: 'Choose a provider under "Add a key", then open "How to get your key" for step-by-step help.',
} as const;

const PLANNED_PRO =
  'Auteur Cloud does not run anything with this key yet; you can store it now and Settings → ' +
  'Connections shows what is planned.';

export const PROVIDER_SETUP_GUIDES: Readonly<Record<string, ProviderSetupGuide>> = Object.freeze({
  openai: {
    id: 'openai',
    name: 'OpenAI',
    powers: 'GPT text models for Tasks and for writing in your workspace studios, and image generation.',
    steps: [
      { text: 'Sign in to the OpenAI API platform and open the API keys page:', link: { label: 'platform.openai.com/api-keys', href: 'https://platform.openai.com/api-keys' } },
      { text: 'Create a new secret key. Name it something you will recognise, such as "Auteur".' },
      { text: 'Copy the key straight away: OpenAI shows it only once.' },
    ],
    billing:
      'OpenAI API usage is billed separately from a ChatGPT subscription. Add credit on the OpenAI billing page before the first call.',
    signUp: { label: 'Create an OpenAI account', href: 'https://platform.openai.com/signup' },
    billingPage: { label: 'OpenAI billing', href: 'https://platform.openai.com/settings/organization/billing/overview' },
    pricingPage: { label: 'OpenAI API pricing', href: 'https://openai.com/api/pricing/' },
    firstUseCost: null,
    keyPage: { label: 'platform.openai.com/api-keys', href: 'https://platform.openai.com/api-keys' },
    pro: 'A key with default permissions works. Auteur checks it by listing your models, which costs nothing.',
    helpPage: { label: 'OpenAI API quickstart', href: 'https://developers.openai.com/api/docs/quickstart' },
    revokePage: { label: 'platform.openai.com/api-keys', href: 'https://platform.openai.com/api-keys' },
  },
  anthropic: {
    id: 'anthropic',
    name: 'Anthropic',
    powers: 'Claude text models for Tasks and for writing in your workspace studios.',
    steps: [
      { text: 'Sign in to the Claude Console and open API keys:', link: { label: 'platform.claude.com/settings/keys', href: 'https://platform.claude.com/settings/keys' } },
      { text: 'Click "Create key". Name it something you will recognise, such as "Auteur", choose an expiration, and set "Linked account" to yourself.' },
      { text: 'Copy the key (it starts with "sk-ant-") straight away: it is shown only once.' },
    ],
    billing:
      'Claude API usage is billed separately from a Claude.ai subscription. Buy credits on the Console billing page before the first call.',
    signUp: { label: 'Create a Claude Console account', href: 'https://platform.claude.com/' },
    billingPage: { label: 'Claude Console billing', href: 'https://platform.claude.com/settings/billing' },
    pricingPage: { label: 'Claude pricing', href: 'https://claude.com/pricing' },
    firstUseCost: null,
    keyPage: { label: 'platform.claude.com/settings/keys', href: 'https://platform.claude.com/settings/keys' },
    pro: 'A personal key works. Do not make it a key that works on several Console workspaces: those need a workspace header that Auteur does not send. If "Create key" is disabled, ask your organization admin.',
    helpPage: { label: 'Get your Claude API key', href: 'https://platform.claude.com/docs/en/get-api-key' },
    revokePage: { label: 'platform.claude.com/settings/keys', href: 'https://platform.claude.com/settings/keys' },
  },
  openrouter: {
    id: 'openrouter',
    name: 'OpenRouter',
    powers: 'One key for many text models (Claude, GPT, Gemini, DeepSeek and more) in Tasks and for writing in your workspace studios.',
    steps: [
      { text: 'Sign in to OpenRouter and open the API keys page:', link: { label: 'openrouter.ai/settings/keys', href: 'https://openrouter.ai/settings/keys' } },
      { text: 'Create a key. Give it a name such as "Auteur" and, if you like, a credit limit (OpenRouter recommends one).' },
      { text: 'Copy the key straight away: it is shown only once.' },
    ],
    billing: 'OpenRouter is pay-as-you-go: buy credits on the OpenRouter credits page before the first call.',
    signUp: { label: 'Create an OpenRouter account', href: 'https://openrouter.ai/sign-up' },
    billingPage: { label: 'OpenRouter credits', href: 'https://openrouter.ai/settings/credits' },
    pricingPage: { label: 'Model prices on OpenRouter', href: 'https://openrouter.ai/models' },
    firstUseCost: null,
    keyPage: { label: 'openrouter.ai/settings/keys', href: 'https://openrouter.ai/settings/keys' },
    pro: 'No scopes to set. A per-key credit limit is a good spend cap; requests are refused once it is used up until you raise it.',
    helpPage: { label: 'OpenRouter authentication', href: 'https://openrouter.ai/docs/api-reference/authentication' },
    revokePage: { label: 'openrouter.ai/settings/keys', href: 'https://openrouter.ai/settings/keys' },
  },
  elevenlabs: {
    id: 'elevenlabs',
    name: 'ElevenLabs',
    powers: 'Narration and voices, multi-voice dialogue, sound effects and music in Audio Canvas and Audiovisual Canvas.',
    steps: [
      { text: 'Sign in to ElevenLabs and open your API keys settings:', link: { label: 'elevenlabs.io/app/settings/api-keys', href: 'https://elevenlabs.io/app/settings/api-keys' } },
      { text: 'Create a new key and name it something you will recognise, such as "Auteur".' },
      { text: 'Copy the key straight away and keep it somewhere safe until you paste it here.' },
    ],
    billing: 'Usage counts against the credits of your ElevenLabs plan. Upgrade or add credits on the ElevenLabs side if you run out.',
    signUp: { label: 'Create an ElevenLabs account', href: 'https://elevenlabs.io/app/sign-up' },
    billingPage: null,
    pricingPage: { label: 'ElevenLabs pricing', href: 'https://elevenlabs.io/pricing' },
    firstUseCost: null,
    keyPage: { label: 'elevenlabs.io/app/settings/api-keys', href: 'https://elevenlabs.io/app/settings/api-keys' },
    pro:
      'If you restrict the key, allow the endpoints you will use (text to speech, text to dialogue, sound effects, music) and reading your user account, which Auteur uses to check the key. A credit quota on the key is a good spend cap. Personal keys need a Full Seat in multi-seat workspaces.',
    helpPage: { label: 'ElevenLabs API keys', href: 'https://elevenlabs.io/docs/overview/administration/workspaces/api-keys' },
    revokePage: { label: 'elevenlabs.io/app/settings/api-keys', href: 'https://elevenlabs.io/app/settings/api-keys' },
  },
  wavespeed: {
    id: 'wavespeed',
    name: 'WaveSpeed',
    powers: 'Text-to-video in Visual Canvas, with Seedance, Kling, Veo and Grok Imagine models.',
    steps: [
      { text: 'Sign in to WaveSpeed and open the API keys page:', link: { label: 'wavespeed.ai/accesskey', href: 'https://wavespeed.ai/accesskey' } },
      { text: 'Click "Generate" to create a new key.' },
      { text: 'Copy the key straight away: WaveSpeed says you won\'t be able to see it again.' },
    ],
    billing:
      'A top-up is not needed to create a key, but generating video uses WaveSpeed credits, and some models need a paid (topped-up) account. Add credit on the WaveSpeed side first.',
    signUp: { label: 'Create a WaveSpeed account', href: 'https://wavespeed.ai/sign-in' },
    billingPage: { label: 'WaveSpeed payment methods', href: 'https://wavespeed.ai/docs/payment-methods' },
    pricingPage: { label: 'WaveSpeed pricing', href: 'https://wavespeed.ai/pricing' },
    firstUseCost: 'WaveSpeed says eligible new users may receive $1 in trial credit for supported models.',
    keyPage: { label: 'wavespeed.ai/accesskey', href: 'https://wavespeed.ai/accesskey' },
    pro: 'No scopes to set; the key\'s creator must have a role that can call the model endpoints. Auteur checks the key by reading your balance, which costs nothing.',
    helpPage: { label: 'WaveSpeed API authentication', href: 'https://wavespeed.ai/docs/api-authentication' },
    revokePage: { label: 'wavespeed.ai/accesskey', href: 'https://wavespeed.ai/accesskey' },
  },
  heygen: {
    id: 'heygen',
    name: 'HeyGen',
    powers: 'Avatar video: a talking presenter that reads your script.',
    steps: [
      { text: 'Sign in to HeyGen and open the API dashboard:', link: { label: 'app.heygen.com/developers/api', href: 'https://app.heygen.com/developers/api' } },
      { text: 'Generate your API key there.' },
      { text: 'Copy the key and keep it somewhere safe until you paste it here.' },
    ],
    billing: 'HeyGen API usage is billed by HeyGen to your account. Check HeyGen\'s API pricing and add funds on the HeyGen side before the first video.',
    signUp: { label: 'Create a HeyGen account', href: 'https://app.heygen.com/signup' },
    billingPage: null,
    pricingPage: { label: 'HeyGen API pricing', href: 'https://www.heygen.com/api-pricing' },
    firstUseCost: null,
    keyPage: { label: 'app.heygen.com/developers/api', href: 'https://app.heygen.com/developers/api' },
    pro: 'No scopes to set. Rotate the key from the same API dashboard; usage is visible at app.heygen.com/developers/usage.',
    helpPage: { label: 'HeyGen API quick start', href: 'https://developers.heygen.com/docs/quick-start' },
    revokePage: { label: 'app.heygen.com/developers/api', href: 'https://app.heygen.com/developers/api' },
  },
  google_gemini: {
    id: 'google_gemini',
    name: 'Google Gemini',
    powers: 'Live now: image analysis (vision scan) in Visual Canvas and Prompt Studio, on your own key, once the key has passed a check. Planned, not available yet: writing, image generation and video with Gemini.',
    steps: [
      { text: 'Sign in with your Google account and open Google AI Studio\'s API keys page:', link: { label: 'aistudio.google.com/apikey', href: 'https://aistudio.google.com/apikey' } },
      { text: 'Click "Create API key".' },
      { text: 'Copy the key.' },
    ],
    billing: 'Gemini API usage is billed by Google. Some use is free; paid use needs billing set up in Google.',
    signUp: { label: 'Google AI Studio', href: 'https://aistudio.google.com/' },
    billingPage: { label: 'Gemini API billing', href: 'https://ai.google.dev/gemini-api/docs/billing' },
    pricingPage: { label: 'Gemini API pricing', href: 'https://ai.google.dev/gemini-api/docs/pricing' },
    firstUseCost: null,
    keyPage: { label: 'aistudio.google.com/apikey', href: 'https://aistudio.google.com/apikey' },
    pro: 'Auteur checks the key with a free model list read, then uses it only for the image scans you run. Allow it for your workspace after adding it.',
    helpPage: { label: 'Using Gemini API keys', href: 'https://ai.google.dev/gemini-api/docs/api-key' },
    revokePage: { label: 'aistudio.google.com/apikey', href: 'https://aistudio.google.com/apikey' },
  },
  perplexity: {
    id: 'perplexity',
    name: 'Perplexity',
    powers: 'Planned for research. Nothing runs in Auteur Cloud with this key yet.',
    steps: [
      { text: 'Sign in to the Perplexity API Console and open the API Keys page:', link: { label: 'console.perplexity.ai/project/keys', href: 'https://console.perplexity.ai/project/keys' } },
      { text: 'Generate a new key.' },
      { text: 'Copy the key.' },
    ],
    billing: 'Perplexity API usage is pay-as-you-go and billed by Perplexity, separately from a Perplexity Pro subscription.',
    signUp: { label: 'Perplexity API Console', href: 'https://console.perplexity.ai/' },
    billingPage: null,
    pricingPage: { label: 'Perplexity API pricing', href: 'https://docs.perplexity.ai/getting-started/pricing' },
    firstUseCost: null,
    keyPage: { label: 'console.perplexity.ai/project/keys', href: 'https://console.perplexity.ai/project/keys' },
    pro: PLANNED_PRO,
    helpPage: { label: 'Perplexity API quickstart', href: 'https://docs.perplexity.ai/getting-started/quickstart' },
    revokePage: { label: 'console.perplexity.ai/project/keys', href: 'https://console.perplexity.ai/project/keys' },
  },
  tavily: {
    id: 'tavily',
    name: 'Tavily',
    powers: 'Planned for web research. Nothing runs in Auteur Cloud with this key yet.',
    steps: [
      { text: 'Sign in to (or create) your account on the Tavily Platform:', link: { label: 'app.tavily.com', href: 'https://app.tavily.com/' } },
      { text: 'Copy one of the API keys shown on your dashboard.' },
    ],
    billing: 'Tavily usage is billed by Tavily. Tavily says every account gets 1,000 free API credits each month, with no card required.',
    signUp: { label: 'Tavily Platform', href: 'https://app.tavily.com/' },
    billingPage: { label: 'Tavily billing', href: 'https://app.tavily.com/billing' },
    pricingPage: { label: 'Tavily pricing', href: 'https://www.tavily.com/pricing' },
    firstUseCost: 'Tavily says 1,000 free API credits are included every month.',
    keyPage: { label: 'app.tavily.com', href: 'https://app.tavily.com/' },
    pro: PLANNED_PRO,
    helpPage: { label: 'Tavily quickstart', href: 'https://docs.tavily.com/documentation/quickstart' },
    revokePage: { label: 'app.tavily.com', href: 'https://app.tavily.com/' },
  },
  fish_audio: {
    id: 'fish_audio',
    name: 'Fish Audio',
    powers: 'Planned for narration and voices in Audio Canvas. Nothing runs in Auteur Cloud with this key yet.',
    steps: [
      { text: 'Sign in to Fish Audio and open the API Keys section:', link: { label: 'fish.audio/app/api-keys', href: 'https://fish.audio/app/api-keys/' } },
      { text: 'Click "Create New Key" and give it a descriptive name, such as "Auteur".' },
      { text: 'Copy the key.' },
    ],
    billing: 'Fish Audio API usage is billed by Fish Audio. Add funds on the Fish Audio billing page; "Out of credits" means the balance is empty.',
    signUp: { label: 'Fish Audio', href: 'https://fish.audio/' },
    billingPage: { label: 'Fish Audio billing', href: 'https://fish.audio/app/billing' },
    pricingPage: null,
    firstUseCost: null,
    keyPage: { label: 'fish.audio/app/api-keys', href: 'https://fish.audio/app/api-keys/' },
    pro: PLANNED_PRO,
    helpPage: { label: 'Fish Audio quickstart', href: 'https://docs.fish.audio/developer-guide/getting-started/quickstart' },
    revokePage: { label: 'fish.audio/app/api-keys', href: 'https://fish.audio/app/api-keys/' },
  },
  fal: {
    id: 'fal',
    name: 'fal',
    powers: 'Planned for image and video generation in Visual Canvas. Nothing runs in Auteur Cloud with this key yet.',
    steps: [
      { text: 'Sign in to fal and open the keys page:', link: { label: 'fal.ai/dashboard/keys', href: 'https://fal.ai/dashboard/keys' } },
      { text: 'Click "Create Key", give it a name you will remember and choose the "API" scope.' },
      { text: 'Copy the key.' },
    ],
    billing: 'fal usage is billed by fal to your account. Add credit on the fal side before the first call.',
    signUp: { label: 'fal', href: 'https://fal.ai/login' },
    billingPage: null,
    pricingPage: { label: 'fal pricing', href: 'https://fal.ai/pricing' },
    firstUseCost: null,
    keyPage: { label: 'fal.ai/dashboard/keys', href: 'https://fal.ai/dashboard/keys' },
    pro: 'Use the "API" scope; "ADMIN" is only for deploying your own apps. For a team key, select the team at the top left first. ' + PLANNED_PRO,
    helpPage: { label: 'fal authentication', href: 'https://fal.ai/docs/model-apis/authentication' },
    revokePage: { label: 'fal.ai/dashboard/keys', href: 'https://fal.ai/dashboard/keys' },
  },
  local: {
    id: 'local',
    name: 'Local endpoint',
    powers: 'For a model server you run yourself. Hosted workspaces do not use it yet.',
    steps: [
      { text: 'There is no provider website for this. Use the key your own server expects; if it needs none, you do not need this entry.' },
    ],
    billing: 'Nothing is billed: it is your own server.',
    signUp: null,
    billingPage: null,
    pricingPage: null,
    firstUseCost: null,
    keyPage: null,
    pro: PLANNED_PRO,
    helpPage: null,
    revokePage: null,
  },
});

/** The guide for a server-listed provider id, or null when there is none. */
export function guideFor(providerId: string | null | undefined): ProviderSetupGuide | null {
  if (!providerId) return null;
  return Object.prototype.hasOwnProperty.call(PROVIDER_SETUP_GUIDES, providerId)
    ? PROVIDER_SETUP_GUIDES[providerId]
    : null;
}
